import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { CompliancesLibraryService } from '../../../../../../services/compliances-library.service';
import { ErrorHandlerService } from '../../../../../../../../../_shared/services/error-handler.service';
import { AssignedAssets, ComplianceFrequency } from '../../../../../../models/compliances.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupLabelPipe } from '../../../../../../../../../_shared/pipes/lookup-label.pipe';
import { translateComplianceFrequency } from '../../../../../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-edit-frequency-modal',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, TranslocoDirective, TranslocoPipe, LookupLabelPipe],
  templateUrl: './edit-frequency-modal.component.html',
  styleUrl: './edit-frequency-modal.component.scss'
})
export class EditFrequencyModalComponent implements OnInit, OnDestroy {
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private complianceLibraryService: CompliancesLibraryService = inject(CompliancesLibraryService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private toastr: ToastrService = inject(ToastrService);
  private transloco: TranslocoService = inject(TranslocoService);
  private cancellation = new Subject<void>();

  @Input() asset!: AssignedAssets;
  @Input() complianceId: number = 0;

  frequencies: ComplianceFrequency[] = [];
  selectedFrequencyId: number | null = null;
  isSaving: boolean = false;

  ngOnInit(): void {
    this.selectedFrequencyId = this.asset.frequencyId ?? null;
    this.loadFrequenciesAsync();
  }

  private async loadFrequenciesAsync(): Promise<void> {
    try {
      const response = await this.complianceLibraryService.getComplianceFrequenciesAsync(this.cancellation);
      this.frequencies = response.data ?? [];
    } catch (error) {
      this.errorService.handleError(error, { showToast: true });
    }
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  async onSave(): Promise<void> {
    if (this.selectedFrequencyId == null || !this.isFrequencyChanged) return;
    this.isSaving = true;
    try {
      await this.complianceLibraryService.editAssignedAssetFrequencyAsync({
        assetId: this.asset.id,
        complianceId: this.complianceId,
        frequencyId: this.selectedFrequencyId
      });
      this.toastr.success(this.transloco.translate('compliance.library.editFrequencyModal.toasts.updated'));
      this.activeModal.close(true);
    } catch (error) {
      this.errorService.handleError(error, {
        showToast: true,
        callback: () => { 
          this.isSaving = false; 
        }
      });
    }
  }

  get isFrequencyChanged(): boolean {
    return this.selectedFrequencyId !== this.asset.frequencyId;
  }

  get selectedFrequencyLabel(): string {
    const selected = this.frequencies.find(f => f.id === this.selectedFrequencyId);
    return selected
      ? translateComplianceFrequency(this.transloco, selected.name)
      : this.transloco.translate('compliance.library.editFrequencyModal.placeholder');
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }
}